import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

export function getQueryKey(orgId: string | undefined, key: string) {
  if (orgId === undefined || orgId === "") {
    // Programming error. This hook should only be called with a valid orgId.
    throw new Error(
      "Invalid orgId. useOrgConfig must be used with a valid orgId.",
    );
  }
  return ["orgConfig", orgId, key];
}

/**
 * Fetch an org config value from the backend.
 *
 * @template T The type of the config value to fetch
 * @param orgId - The org to fetch the config value for
 * @param key - The config key to fetch
 * @returns The config value
 */

export function useOrgConfig<T = unknown>(
  orgId: string | undefined,
  key: string,
) {
  const { configService } = useDomainServices();

  return useQuery({
    queryKey: getQueryKey(orgId, key),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Invalid orgId"));
      }

      return configService.getOrgConfig<T>(orgId, key, {
        abortSignal: signal,
      });
    },
    enabled: orgId !== undefined,
  });
}
